import { useCallback, useEffect, useRef, useState } from 'react';
import { assetsMediaContentConfigPartialUpdate, MediaContentConfig } from '@goldcast/api/content';
import { NewContentModalStep } from './constants';
import WorkspaceUploadStep from './WorkspaceUploadStep';
import ClipCurationStep from './ClipCurationStep';
import { UploadPreferences, storeLocalUploadPreferences } from './localUploadPreferences.util';
import { UPLOAD_EDITOR_READY_EVENT } from '../ContentReadinessPopup/constants';
import ContentDialog from '@/Pages/Sessions/uiComponents/ContentDialog/ContentDialog';
import type { AspectRatioValue } from '@/components/molecules/AspectRatioTiles/constants';
import Button from '@/components/atoms/Button/Button';
import useContentUpload from '@/hooks/useContentUpload';
import usePlatformBrandKits from '@/hooks/usePlatformBrandKits';
import useAIAddon from '@/hooks/useAIAddon';
import { showErrorToast } from '@/libs/toast/toast';
import { useAppContext } from '@/context/AppContext/AppContext';
import { ContentStatesEnum } from '@/Pages/VideoImportPage/constants';
import { isSimplifiedUploadEnabled } from '@/stores/clip';

/**
 * The simplified "New content" modal. Rendered persistently at the admin-app shell
 * so the background upload survives navigation. Open/close is controlled by the federated
 * wrapper, which owns the `OPEN_NEW_CONTENT_MODAL_EVENT` listener (so opening never races the
 * island's async bootstrap).
 *
 * Two screens:
 *  1. Workspace + file upload (file-only, upload runs in the background).
 *  2. Brand kit (color + font) + aspect ratio. Save persists via `upload_preferences` (neutral,
 *     Phase 2); Skip sends nothing and the backend applies its defaults.
 */
const NewContentModal = ({
  isOpen,
  onClose,
  onSuccess
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (videoLibraryId: string) => void;
}) => {
  const { logger } = useAppContext();
  const hasAIAddon = useAIAddon();
  // Shared brand-kit store (ClipCurationStep reads the same snapshot); block Save until kits finish loading.
  const { isLoading: isBrandKitLoading } = usePlatformBrandKits();

  const [step, setStep] = useState<NewContentModalStep>(NewContentModalStep.WorkspaceUpload);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');
  const [currentContentUploadId, setCurrentContentUploadId] = useState<string>();
  const [colors, setColors] = useState<string[]>([]);
  const [selectedFontUrl, setSelectedFontUrl] = useState<string>('');
  // Font display name isn't consumed here yet (upload_preferences only persists the font URL) —
  // captured so the callback signature matches ClipCurationStep's `onFontChange(url, name)`.
  const [, setSelectedFontName] = useState<string>('');
  const [aspectRatios, setAspectRatios] = useState<AspectRatioValue[]>([]);

  const { onFilesUpload, uploadContent, getContentUploadById, currentMediaContentConfig, createMediaContentConfig } =
    useContentUpload(onSuccess, selectedWorkspaceId);

  const currentUpload = getContentUploadById(currentContentUploadId);
  const hasUploadStarted = Boolean(currentContentUploadId);

  // Dedupe the failure toast: the store re-renders on every progress tick, so only toast when the
  // error message actually changes (and reset it per upload so a later identical failure still shows).
  const shownUploadErrorRef = useRef<string>();

  const resetState = useCallback(() => {
    setStep(NewContentModalStep.WorkspaceUpload);
    setSelectedWorkspaceId('');
    setCurrentContentUploadId(undefined);
    setColors([]);
    setSelectedFontUrl('');
    setSelectedFontName('');
    setAspectRatios([]);
    shownUploadErrorRef.current = undefined;
  }, []);

  // Reset to a clean state each time the modal is opened.
  useEffect(() => {
    if (isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  const closeModal = useCallback(() => {
    // Closing does not cancel the in-flight upload — it keeps running in this shell island.
    onClose();
  }, [onClose]);

  // Surface background-upload failures as a toast and close the modal. `uploadContent` swallows the
  // error internally and records it on the store item (via `onUploadFailed`), so the modal's own catch
  // never fires — watch the recorded error instead.
  useEffect(() => {
    const message = currentUpload.error?.message;
    if (message && shownUploadErrorRef.current !== message) {
      shownUploadErrorRef.current = message;
      showErrorToast(message);
      closeModal();
    }
  }, [currentUpload.error, closeModal]);

  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      try {
        const upload = await onFilesUpload(files);
        if (!upload) {
          return;
        }
        setCurrentContentUploadId(upload.contentId);
        // Fire-and-forget: the upload runs in the background while the user sets preferences.
        uploadContent(upload)
          .then(content => {
            if (content) {
              if (isSimplifiedUploadEnabled()) {
                // Popup is the feedback surface for simplified-upload orgs — advance it to
                // PROCESSING immediately rather than waiting for the first transcription WS
                // (which only fires when the batch transcription job starts). The toast is
                // skipped because showing both would be redundant.
                document.dispatchEvent(
                  new CustomEvent(UPLOAD_EDITOR_READY_EVENT, {
                    detail: { content_id: upload.contentId, upload_editor_status: 'PROCESSING' }
                  })
                );
              } else {
                onSuccess?.(content?.video_library_id ?? '');
              }
            }
          })
          .catch(error => logger.error('New content upload failed', error));
      } catch (error) {
        logger.error('New content upload failed', error);
      }
    },
    [onFilesUpload, uploadContent, onSuccess, logger]
  );

  const persistBranding = useCallback(() => {
    if (!currentContentUploadId) {
      return;
    }
    const uploadPreferences: UploadPreferences = {
      // Backend derives the single colour it needs (currently the first entry) from this list.
      colours: colors.length ? colors : undefined,
      font_location: selectedFontUrl || undefined,
      aspect_ratios: aspectRatios
    };
    // Store locally so `createMediaContentConfig` (called during upload) picks it up, and
    // patch the server config directly if it already exists — mirrors the legacy flow.
    storeLocalUploadPreferences(currentContentUploadId, uploadPreferences);

    // Mirror legacy `storeMediaContentConfig`'s guard (VideoImportContext.tsx): until the
    // upload has reached the Processing state, `useContentUpload`'s own post-upload flow has
    // not yet issued its `createMediaContentConfig` call. Bail out here so we don't fire a
    // second, independent create for the same content — the local upload preferences above are
    // already in place for that call to pick up once it does run.
    if (currentUpload.contentState !== ContentStatesEnum.Processing) {
      return;
    }

    if (currentMediaContentConfig?.id) {
      assetsMediaContentConfigPartialUpdate({
        id: currentMediaContentConfig.id,
        body: { upload_preferences: uploadPreferences } as Partial<MediaContentConfig>
      }).catch(error => logger.error('Failed to persist branding for new content upload', error));
    } else {
      createMediaContentConfig(currentContentUploadId);
    }
  }, [
    currentContentUploadId,
    colors,
    selectedFontUrl,
    aspectRatios,
    currentUpload.contentState,
    currentMediaContentConfig,
    createMediaContentConfig,
    logger
  ]);

  const handleSave = useCallback(() => {
    persistBranding();
    closeModal();
  }, [persistBranding, closeModal]);

  const handleSkip = useCallback(() => {
    // Skip persists nothing — the backend applies its defaults.
    closeModal();
  }, [closeModal]);

  const isWorkspaceStep = step === NewContentModalStep.WorkspaceUpload;

  return (
    <ContentDialog
      isOpen={isOpen}
      setIsOpen={closeModal}
      size="large"
      title={isWorkspaceStep ? 'Upload Video' : 'Select Clip Curation Preferences'}
      titleClassName="pt-3 pl-6 text-base font-medium leading-6"
      panelClassNames="!w-[42rem] !overflow-hidden flex flex-col"
      showDivider={false}
    >
      {/*
        The panel sizes to its content (capped at 75vh) with an internal scroll region + a pinned
        footer. On screen 2 the brand-kit/font/colour pickers open as fixed-position overlays (in
        ClipCurationStep) that float on top of the modal content, so the modal keeps its content
        height instead of growing to fit an open picker.
      */}
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Screen 2 (brand kit) hides its scrollbar; the pickers overlay on top rather than reserving space. */}
        <div className={`min-h-0 flex-1 overflow-y-auto p-6 pt-1 ${isWorkspaceStep ? '' : 'no-scrollbar'}`}>
          {/*
            The workspace/upload step stays mounted across step navigation (toggled with `hidden`,
            not conditionally rendered) so its `useWorkspace` list, selection, and generated
            thumbnail survive going Next → Back without a reload/refetch flash.
          */}
          <div className={isWorkspaceStep ? '' : 'hidden'}>
            <WorkspaceUploadStep
              selectedWorkspaceId={selectedWorkspaceId}
              onWorkspaceChange={setSelectedWorkspaceId}
              onFilesSelected={handleFilesSelected}
              uploadFile={currentUpload.file}
              uploadFileName={currentUpload.file?.name ?? currentUpload.title}
              uploadPercentage={currentUpload.totalPercentageUploaded ?? 0}
              hasUploadStarted={hasUploadStarted}
            />
          </div>

          {/*
            The clip-curation step also stays mounted (toggled with `hidden`, not conditionally
            rendered) so a font uploaded here survives going Back → Next without unmounting — no
            need to refetch it from the backend to restore the chip.
          */}
          <div className={isWorkspaceStep ? 'hidden' : ''}>
            <ClipCurationStep
              contentId={currentContentUploadId}
              colors={colors}
              onColorsChange={setColors}
              onFontChange={(url, name) => {
                setSelectedFontUrl(url);
                setSelectedFontName(name);
              }}
              aspectRatios={aspectRatios}
              onAspectRatiosChange={setAspectRatios}
              uploadFile={currentUpload.file}
              uploadFileName={currentUpload.file?.name ?? currentUpload.title}
              uploadPercentage={currentUpload.totalPercentageUploaded ?? 0}
            />
          </div>
        </div>

        <div className="border-t border-slate-200 p-4">
          {isWorkspaceStep ? (
            <div className="flex items-center justify-end gap-4">
              {/*
                Clip curation only styles AI-generated clips, so orgs without the AI add-on never
                reach that step — the upload finishes here and the backend applies its defaults.
              */}
              <Button
                variation="filled"
                trackingId="new-content-next-button"
                disabled={!hasUploadStarted}
                onClick={hasAIAddon ? () => setStep(NewContentModalStep.ClipCuration) : handleSkip}
              >
                {hasAIAddon ? 'Next' : 'Done'}
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <Button
                variation="outline"
                trackingId="new-content-back-button"
                onClick={() => setStep(NewContentModalStep.WorkspaceUpload)}
              >
                Back
              </Button>
              <div className="flex gap-2">
                <Button variation="outline" trackingId="new-content-skip-button" onClick={handleSkip}>
                  Skip
                </Button>
                <Button
                  variation="filled"
                  trackingId="new-content-save-button"
                  disabled={isBrandKitLoading}
                  onClick={handleSave}
                >
                  Save
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </ContentDialog>
  );
};

export default NewContentModal;
